/**
 * Cloud-provider catalog and per-scope overrides.
 *
 * The catalog mirrors the clouds SkyPilot supports natively (see
 * https://docs.skypilot.co/en/stable/getting-started/installation.html#appendix-cloud-access-for-local-skypilot).
 * Each provider declares the configuration fields the operator must
 * supply (API keys, service-account JSON, kubeconfig, ...). Secret
 * fields are AES-GCM encrypted server-side and never round-trip to the
 * browser; this module surfaces only `key_last4` and a list of
 * "configured" secret-field names.
 *
 * At each scope (global / service / pool) the operator can either
 * inherit the parent's setting or override:
 *   - whether the provider is enabled at this level
 *   - any of the configuration field values
 *
 * Persistence lives in the `providers` table on the controller; this
 * module is just the React Query veneer that fetches the rows and
 * exposes mutation hooks for the WebUI.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  ProviderOut,
  ProviderSkyCheckStatus,
  ProviderUpsertBody,
} from "@/api/types";

// ---- catalog -------------------------------------------------------------

export type ProviderKind =
  // compute clouds
  | "aws"
  | "gcp"
  | "azure"
  | "oci"
  | "runpod"
  | "lambda"
  | "paperspace"
  | "vast"
  | "fluidstack"
  | "cudo"
  | "shadeform"
  | "ibm"
  | "scp"
  | "vsphere"
  | "nebius"
  | "primeintellect"
  | "seeweb"
  | "verda"
  // kubernetes-style targets
  | "kubernetes"
  | "slurm"
  | "coreweave"
  | "togetherai"
  // object storage
  | "cloudflare"
  | "vastdata"
  // model registries (not strictly SkyPilot but useful for replicas)
  | "huggingface"
  // container image registries (pulled by SkyPilot at VM provisioning time
  // via SKYPILOT_DOCKER_{USERNAME,PASSWORD,SERVER} env injection)
  | "ghcr"
  | "dockerhub"
  | "containerregistry";

export type ProviderCategory =
  | "compute"
  | "kubernetes"
  | "storage"
  | "registry"
  | "container_registry";

export type FieldType = "text" | "password" | "textarea" | "bool";

export interface ProviderField {
  /** Identifier persisted in `ProviderEntry.values`. */
  key: string;
  label: string;
  type: FieldType;
  hint?: string;
  optional?: boolean;
  /**
   * When true the value is treated as a secret. The form only persists
   * the last 4 characters; the controller is expected to keep the
   * actual secret in its secret manager.
   */
  secret?: boolean;
  placeholder?: string;
}

/**
 * One placement "flavour" within a provider kind.
 *
 * Mirrors the Python-side ``controller/skypilot/variants.py``
 * declaration; the renderer reads ``use_spot`` to populate
 * ``Resources.use_spot`` and (RunPod only) ``runpod_cloud_type`` to
 * splice the ``_COMMUNITY`` / ``_SECURE`` instance_type suffix.
 *
 * AWS / GCP / Azure / OCI ship two variants (``ondemand`` / ``spot``);
 * RunPod ships ``secure`` (default) and ``community``; every other
 * compute cloud ships a single ``default`` variant so the data model
 * stays uniform across kinds. Kinds without a placement axis
 * (kubernetes, slurm, object stores, registries) declare no variants.
 */
export interface ProviderVariant {
  /** Wire identifier; used as the ``values_plain`` key suffix. */
  key: string;
  label: string;
  /** SkyPilot ``Resources.use_spot`` value this variant maps onto. */
  useSpot: boolean;
  /**
   * RunPod-specific GraphQL value (``"COMMUNITY"`` / ``"SECURE"``)
   * the SkyPilot adapter splices into the GPU instance_type's third
   * underscore segment. ``undefined`` for every cloud other than
   * RunPod.
   */
  runpodCloudType?: "COMMUNITY" | "SECURE";
  /**
   * True for the variant a fresh provider row should pre-enable. The
   * legacy unsuffixed ``enabled_regions`` value migrates into this
   * variant's slot on the first alembic upgrade.
   */
  isDefault?: boolean;
}

export interface ProviderMeta {
  kind: ProviderKind;
  label: string;
  hint: string;
  /**
   * Set when the provider is currently NOT usable end-to-end despite
   * being configurable (e.g. upstream SkyPilot bugs). Rendered as a
   * warning pill + note on the provider row, and the row is greyed
   * out, so an operator can't waste hours discovering it the hard way.
   * Cleared when the upstream fix ships.
   */
  knownIssue?: string;
  category: ProviderCategory;
  /** Anchor under the SkyPilot installation guide. */
  docsAnchor?: string;
  fields: ProviderField[];
  /** Field whose last-4 is used as the credential summary in tables. */
  primaryField: string;
  /**
   * When true, this provider has a meaningful notion of "region" and
   * the catalog row should expose a per-scope region allow-list (see
   * `enabled_regions` in `ProviderEntry.values`). Compute clouds get
   * regions; kubernetes / registries / object-storage don't.
   *
   * Region *suggestions* are not declared here — they're fetched live
   * from the controller (`GET /providers/regions/{kind}` and the
   * sibling `/zones` endpoint), which in turn read SkyPilot's catalog.
   * That keeps a single source of truth: any time SkyPilot adds a
   * region or zone we pick it up automatically after a controller
   * restart, with zero frontend work.
   */
  supportsRegions?: boolean;
  /**
   * Variants this kind exposes. Empty / undefined for kinds without a
   * placement axis. The first variant marked ``isDefault: true`` is
   * what the WebUI's variant picker pre-selects on a fresh row, and
   * what the controller-side migration folds the legacy unsuffixed
   * ``enabled_regions`` value into.
   *
   * P0 does NOT yet surface variant tabs in the UI — every provider
   * row reads/writes its default variant transparently and the data
   * layer threads the variant key through ``getEntryRegions`` /
   * ``regionsToValuePatch`` so P1 (RunPod community/secure) and P2
   * (AWS/GCP/Azure spot) can flip the tabs on without re-shaping
   * persistence.
   */
  variants?: ProviderVariant[];
}

const SKY_DOCS = "https://docs.skypilot.co/en/stable/getting-started/installation.html";

export function providerDocsUrl(meta: ProviderMeta): string {
  return meta.docsAnchor ? `${SKY_DOCS}#${meta.docsAnchor}` : SKY_DOCS;
}

// ---- variant declarations -----------------------------------------------
//
// Kept in lockstep (by hand) with ``controller/skypilot/variants.py``.
// A new compute cloud added on the Python side should mirror its
// variants here so the WebUI's data layer stores under the right
// ``values_plain`` keys.

const SPOT_AWS_VARIANTS: ProviderVariant[] = [
  { key: "ondemand", label: "On-demand", useSpot: false, isDefault: true },
  { key: "spot", label: "Spot", useSpot: true },
];

const RUNPOD_VARIANTS: ProviderVariant[] = [
  {
    key: "secure",
    label: "Secure cloud",
    useSpot: false,
    runpodCloudType: "SECURE",
    isDefault: true,
  },
  {
    key: "community",
    label: "Community cloud",
    useSpot: false,
    runpodCloudType: "COMMUNITY",
  },
];

const DEFAULT_ONLY_VARIANTS: ProviderVariant[] = [
  { key: "default", label: "Default", useSpot: false, isDefault: true },
];

export function variantsForKind(kind: ProviderKind): ProviderVariant[] {
  return PROVIDER_META[kind]?.variants ?? [];
}

export function defaultVariantForKind(
  kind: ProviderKind,
): ProviderVariant | null {
  return variantsForKind(kind).find((v) => v.isDefault) ?? null;
}

export const PROVIDER_META: Record<ProviderKind, ProviderMeta> = {
  aws: {
    kind: "aws",
    label: "AWS",
    hint: "EC2 + Capacity Reservations",
    category: "compute",
    docsAnchor: "aws",
    primaryField: "secret_access_key",
    supportsRegions: true,
    variants: SPOT_AWS_VARIANTS,
    fields: [
      { key: "access_key_id", label: "Access key ID", type: "text" },
      {
        key: "secret_access_key",
        label: "Secret access key",
        type: "password",
        secret: true,
      },
      {
        key: "session_token",
        label: "Session token",
        type: "password",
        secret: true,
        optional: true,
        hint: "Only required for AWS SSO / temporary credentials.",
      },
      {
        key: "region",
        label: "Default region",
        type: "text",
        optional: true,
        placeholder: "us-east-1",
      },
    ],
  },
  gcp: {
    kind: "gcp",
    label: "GCP",
    hint: "Compute Engine / Spot",
    category: "compute",
    docsAnchor: "gcp",
    primaryField: "service_account_json",
    supportsRegions: true,
    variants: SPOT_AWS_VARIANTS,
    fields: [
      {
        key: "service_account_json",
        label: "Service-account JSON",
        type: "textarea",
        secret: true,
        hint: "Paste the JSON key downloaded from IAM → Service Accounts.",
      },
      {
        key: "project_id",
        label: "Project ID",
        type: "text",
        placeholder: "my-gcp-project",
      },
    ],
  },
  azure: {
    kind: "azure",
    label: "Azure",
    hint: "ND/NC GPU VMs",
    category: "compute",
    docsAnchor: "azure",
    primaryField: "client_secret",
    supportsRegions: true,
    variants: SPOT_AWS_VARIANTS,
    fields: [
      { key: "subscription_id", label: "Subscription ID", type: "text" },
      { key: "tenant_id", label: "Tenant ID", type: "text" },
      { key: "client_id", label: "Client ID", type: "text" },
      {
        key: "client_secret",
        label: "Client secret",
        type: "password",
        secret: true,
      },
    ],
  },
  oci: {
    kind: "oci",
    label: "Oracle Cloud (OCI)",
    hint: "Bare-metal & GPU shapes",
    category: "compute",
    docsAnchor: "oci",
    primaryField: "api_key_pem",
    supportsRegions: true,
    variants: SPOT_AWS_VARIANTS,
    fields: [
      { key: "tenancy_ocid", label: "Tenancy OCID", type: "text" },
      { key: "user_ocid", label: "User OCID", type: "text" },
      {
        key: "fingerprint",
        label: "Fingerprint",
        type: "text",
        placeholder: "aa:bb:cc:dd:ee:ff:gg:hh:ii:jj:kk:ll:mm:nn:oo:pp",
      },
      {
        key: "region",
        label: "Region",
        type: "text",
        placeholder: "us-sanjose-1",
      },
      {
        key: "api_key_pem",
        label: "API signing key (PEM)",
        type: "textarea",
        secret: true,
        hint: "Contents of ~/.oci/oci_api_key.pem.",
      },
      {
        key: "compartment_ocid",
        label: "Compartment OCID",
        type: "text",
        optional: true,
      },
    ],
  },
  runpod: {
    kind: "runpod",
    label: "RunPod",
    hint: "On-demand GPUs (H100 / A100 / L40 / L4 …)",
    category: "compute",
    docsAnchor: "runpod",
    primaryField: "api_key",
    supportsRegions: true,
    variants: RUNPOD_VARIANTS,
    fields: [
      { key: "api_key", label: "API key", type: "password", secret: true },
    ],
  },
  lambda: {
    kind: "lambda",
    label: "Lambda Cloud",
    hint: "Lambda Labs on-demand GPU cloud",
    category: "compute",
    docsAnchor: "lambda-cloud",
    primaryField: "api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "api_key", label: "API key", type: "password", secret: true },
    ],
  },
  paperspace: {
    kind: "paperspace",
    label: "Paperspace",
    hint: "GPU-accelerated VMs",
    category: "compute",
    docsAnchor: "paperspace",
    primaryField: "api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "api_key", label: "API key", type: "password", secret: true },
    ],
  },
  vast: {
    kind: "vast",
    label: "Vast.ai",
    hint: "Low-cost spot GPU marketplace",
    // 2026-08-20: Vast deprecated its v0 instance-listing API and the
    // pinned SkyPilot nightly still calls it — running instances become
    // invisible to sky (endless provisioning wait / SkyServe launch
    // storms), and instance creation itself crashes on an SDK
    // incompatibility. Reported upstream (skypilot#10517, #10518,
    // comment on #9362); remove this once the fixes ship in the pinned
    // nightly. Details: docs/proposals/capacity-bank-pod-lease.md §11.
    knownIssue:
      "Currently unusable: upstream SkyPilot/Vast API bugs (instances " +
      "become invisible to sky and can launch-storm). Keep disabled " +
      "until the upstream fixes land (skypilot#10517 / #10518).",
    category: "compute",
    docsAnchor: "vast",
    primaryField: "api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "api_key", label: "API key", type: "password", secret: true },
    ],
  },
  fluidstack: {
    kind: "fluidstack",
    label: "Fluidstack",
    hint: "Distributed low-cost GPUs",
    category: "compute",
    docsAnchor: "fluidstack",
    primaryField: "api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "api_key", label: "API key", type: "password", secret: true },
    ],
  },
  cudo: {
    kind: "cudo",
    label: "Cudo Compute",
    hint: "Green-energy GPU cloud",
    category: "compute",
    docsAnchor: "cudo-compute",
    primaryField: "api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "api_key", label: "API key", type: "password", secret: true },
      { key: "project_id", label: "Project ID", type: "text" },
      {
        key: "billing_account_id",
        label: "Billing account ID",
        type: "text",
        optional: true,
      },
    ],
  },
  shadeform: {
    kind: "shadeform",
    label: "Shadeform",
    hint: "Multi-cloud GPU marketplace",
    category: "compute",
    docsAnchor: "shadeform",
    primaryField: "api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "api_key", label: "API key", type: "password", secret: true },
    ],
  },
  ibm: {
    kind: "ibm",
    label: "IBM Cloud",
    hint: "IBM VPC service",
    category: "compute",
    docsAnchor: "ibm",
    primaryField: "iam_api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      {
        key: "iam_api_key",
        label: "IAM API key",
        type: "password",
        secret: true,
      },
      { key: "resource_group_id", label: "Resource group ID", type: "text" },
      {
        key: "cos_access_key_id",
        label: "COS access key ID",
        type: "text",
        optional: true,
        hint: "Required only to use IBM Cloud Object Storage.",
      },
      {
        key: "cos_secret_access_key",
        label: "COS secret access key",
        type: "password",
        secret: true,
        optional: true,
      },
    ],
  },
  scp: {
    kind: "scp",
    label: "Samsung Cloud (SCP)",
    hint: "Samsung Cloud Platform",
    category: "compute",
    docsAnchor: "scp-samsung-cloud-platform",
    primaryField: "secret_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "access_key", label: "Access key", type: "text" },
      {
        key: "secret_key",
        label: "Secret key",
        type: "password",
        secret: true,
      },
      { key: "project_id", label: "Project ID", type: "text" },
    ],
  },
  vsphere: {
    kind: "vsphere",
    label: "VMware vSphere",
    hint: "Self-hosted vSphere clusters",
    category: "compute",
    docsAnchor: "vmware-vsphere",
    primaryField: "password",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      {
        key: "vcenter",
        label: "vCenter host",
        type: "text",
        placeholder: "vcenter.example.com",
      },
      { key: "username", label: "Username", type: "text" },
      { key: "password", label: "Password", type: "password", secret: true },
      {
        key: "skip_verification",
        label: "Skip TLS verification",
        type: "bool",
        optional: true,
        hint: "Disable certificate validation (only for self-signed vCenters).",
      },
      {
        key: "clusters",
        label: "Allowed clusters",
        type: "text",
        optional: true,
        placeholder: "cluster-a, cluster-b (blank = all)",
      },
    ],
  },
  nebius: {
    kind: "nebius",
    label: "Nebius",
    hint: "AI-first European GPU cloud",
    category: "compute",
    docsAnchor: "nebius",
    primaryField: "iam_token",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      {
        key: "iam_token",
        label: "IAM access token",
        type: "password",
        secret: true,
        hint: "Output of `nebius iam get-access-token`.",
      },
      { key: "tenant_id", label: "Tenant ID", type: "text" },
      {
        key: "project_id",
        label: "Project ID",
        type: "text",
        optional: true,
      },
      {
        key: "fabric",
        label: "Fabric",
        type: "text",
        optional: true,
        placeholder: "fabric-2",
      },
    ],
  },
  primeintellect: {
    kind: "primeintellect",
    label: "Prime Intellect",
    hint: "Distributed training compute",
    category: "compute",
    docsAnchor: "prime-intellect",
    primaryField: "api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "api_key", label: "API key", type: "password", secret: true },
      { key: "team_id", label: "Team ID", type: "text", optional: true },
    ],
  },
  seeweb: {
    kind: "seeweb",
    label: "Seeweb",
    hint: "European GPU cloud",
    category: "compute",
    docsAnchor: "seeweb",
    primaryField: "api_key",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "api_key", label: "API token", type: "password", secret: true },
    ],
  },
  verda: {
    kind: "verda",
    label: "Verda",
    hint: "Sovereign European GPU cloud",
    category: "compute",
    docsAnchor: "verda",
    primaryField: "client_secret",
    supportsRegions: true,
    variants: DEFAULT_ONLY_VARIANTS,
    fields: [
      { key: "client_id", label: "Client ID", type: "text" },
      {
        key: "client_secret",
        label: "Client secret",
        type: "password",
        secret: true,
      },
    ],
  },

  kubernetes: {
    kind: "kubernetes",
    label: "Kubernetes",
    hint: "Any kubeconfig-reachable cluster (EKS / GKE / on-prem …). Leave the kubeconfig blank to use the controller's own in-cluster ServiceAccount (SkyServe controller on this GKE cluster — see docs/serve-controller-on-gke.md).",
    category: "kubernetes",
    docsAnchor: "kubernetes",
    primaryField: "kubeconfig",
    fields: [
      {
        key: "kubeconfig",
        label: "Kubeconfig",
        type: "textarea",
        secret: true,
        optional: true,
        hint: "Full contents of ~/.kube/config (will be merged on the controller). Optional: blank = in-cluster auth.",
      },
      {
        key: "context",
        label: "Default context",
        type: "text",
        optional: true,
        placeholder: "leave blank to use current-context",
      },
      {
        key: "namespace",
        label: "Default namespace",
        type: "text",
        optional: true,
        placeholder: "default",
      },
    ],
  },
  slurm: {
    kind: "slurm",
    label: "Slurm",
    hint: "Slurm login node over SSH",
    category: "kubernetes",
    docsAnchor: "slurm",
    primaryField: "ssh_private_key",
    fields: [
      {
        key: "ssh_config",
        label: "SSH config snippet",
        type: "textarea",
        hint: "Contents that should go into ~/.slurm/config.",
        placeholder:
          "Host mycluster\n  HostName login.mycluster.example.com\n  User myusername",
      },
      {
        key: "ssh_private_key",
        label: "SSH private key",
        type: "textarea",
        secret: true,
      },
    ],
  },
  coreweave: {
    kind: "coreweave",
    label: "CoreWeave (CKS)",
    hint: "CoreWeave Kubernetes Service",
    category: "kubernetes",
    docsAnchor: "coreweave",
    primaryField: "kubeconfig",
    fields: [
      {
        key: "kubeconfig",
        label: "Kubeconfig",
        type: "textarea",
        secret: true,
        hint: "Download from the CoreWeave console (Kubernetes → API tokens).",
      },
      {
        key: "infiniband",
        label: "Enable InfiniBand",
        type: "bool",
        optional: true,
        hint: "Sets `network_tier: best` on submitted tasks.",
      },
    ],
  },
  togetherai: {
    kind: "togetherai",
    label: "Together AI",
    hint: "Together AI Instant Clusters (Kubernetes)",
    category: "kubernetes",
    docsAnchor: "together-ai",
    primaryField: "kubeconfig",
    fields: [
      {
        key: "kubeconfig",
        label: "Kubeconfig",
        type: "textarea",
        secret: true,
        hint: "Issued by Together when you create an Instant Cluster.",
      },
    ],
  },

  cloudflare: {
    kind: "cloudflare",
    label: "Cloudflare R2",
    hint: "S3-compatible object storage (zero egress fees)",
    category: "storage",
    docsAnchor: "cloudflare-r2",
    primaryField: "secret_access_key",
    fields: [
      { key: "access_key_id", label: "Access key ID", type: "text" },
      {
        key: "secret_access_key",
        label: "Secret access key",
        type: "password",
        secret: true,
      },
      {
        key: "account_id",
        label: "Cloudflare account ID",
        type: "text",
        hint: "Found at the bottom of the R2 dashboard.",
      },
    ],
  },
  vastdata: {
    kind: "vastdata",
    label: "VastData",
    hint: "S3-compatible object storage (VastData, ≠ Vast.ai)",
    category: "storage",
    docsAnchor: "vastdata-object-storage",
    primaryField: "secret_access_key",
    fields: [
      { key: "access_key_id", label: "Access key ID", type: "text" },
      {
        key: "secret_access_key",
        label: "Secret access key",
        type: "password",
        secret: true,
      },
      {
        key: "endpoint_url",
        label: "S3 endpoint URL",
        type: "text",
        placeholder: "https://s3.example.vastdata.com",
      },
    ],
  },

  huggingface: {
    kind: "huggingface",
    label: "Hugging Face",
    hint: "Model + dataset registry (used at replica boot)",
    category: "registry",
    primaryField: "token",
    fields: [
      {
        key: "token",
        label: "HF access token",
        type: "password",
        secret: true,
        hint: "Used by replicas to download gated model artifacts.",
      },
    ],
  },

  // ---- Container image registries --------------------------------------
  //
  // Used at SkyPilot VM provisioning time to `docker login` against a
  // private registry before pulling `Service.workload.image`. The
  // controller injects SKYPILOT_DOCKER_{USERNAME,PASSWORD,SERVER} envs
  // on rendered services whose image host matches the registry's server
  // (matching is done on bare host, e.g. `ghcr.io` or `docker.io`).
  //
  // The three rows below cover the common cases without forcing an
  // operator to remember the server hostname for the well-known ones:
  //
  //   - `ghcr`              -> server defaults to `ghcr.io`
  //   - `dockerhub`         -> server defaults to `docker.io`
  //   - `containerregistry` -> free-form server, for self-hosted /
  //                            third-party registries (Quay, GitLab,
  //                            Harbor, GAR, ECR-via-static-creds, …)
  //
  // Username / password fields use the *registry's* notion of those:
  // for GHCR the username is your GitHub username (or org) and the
  // password is a PAT with `read:packages`; for Docker Hub the
  // password is a Personal Access Token from
  // https://hub.docker.com/settings/security.
  ghcr: {
    kind: "ghcr",
    label: "GitHub Container Registry (ghcr.io)",
    hint: "Pull private images from ghcr.io (e.g. backstage-loadtest)",
    category: "container_registry",
    primaryField: "password",
    fields: [
      {
        key: "username",
        label: "GitHub username (or org)",
        type: "text",
        placeholder: "spiral-ai",
        hint: "Account that owns the Personal Access Token below.",
      },
      {
        key: "password",
        label: "Personal Access Token",
        type: "password",
        secret: true,
        hint: "Classic PAT with at least `read:packages` (or `write:packages` to push).",
      },
    ],
  },
  dockerhub: {
    kind: "dockerhub",
    label: "Docker Hub (docker.io)",
    hint: "Pull private images from hub.docker.com",
    category: "container_registry",
    primaryField: "password",
    fields: [
      {
        key: "username",
        label: "Docker Hub username",
        type: "text",
        placeholder: "myuser",
      },
      {
        key: "password",
        label: "Access token",
        type: "password",
        secret: true,
        hint: "Generate at https://hub.docker.com/settings/security — Docker Hub no longer accepts the account password here.",
      },
    ],
  },
  containerregistry: {
    kind: "containerregistry",
    label: "Other container registry",
    hint: "Quay / GitLab / Harbor / self-hosted … any docker-login compatible registry",
    category: "container_registry",
    primaryField: "password",
    fields: [
      {
        key: "server",
        label: "Registry host",
        type: "text",
        placeholder: "registry.example.com",
        hint: "Bare hostname (no scheme, no path). Must match the prefix of the workload image.",
      },
      {
        key: "username",
        label: "Username",
        type: "text",
      },
      {
        key: "password",
        label: "Password / token",
        type: "password",
        secret: true,
      },
    ],
  },
};

export const PROVIDER_KINDS: ProviderKind[] = [
  // compute clouds
  "aws",
  "gcp",
  "azure",
  "oci",
  "runpod",
  "lambda",
  "paperspace",
  "vast",
  "fluidstack",
  "cudo",
  "shadeform",
  "ibm",
  "scp",
  "vsphere",
  "nebius",
  "primeintellect",
  "seeweb",
  "verda",
  // kubernetes-style
  "kubernetes",
  "slurm",
  "coreweave",
  "togetherai",
  // storage
  "cloudflare",
  "vastdata",
  // registry
  "huggingface",
  // container image registries
  "ghcr",
  "dockerhub",
  "containerregistry",
];

export const PROVIDER_CATEGORIES: Array<{
  category: ProviderCategory;
  title: string;
  blurb: string;
}> = [
  {
    category: "compute",
    title: "Compute clouds",
    blurb:
      "VM-based GPU clouds reachable via the provider's native API. SkyPilot dispatches one cluster per replica here.",
  },
  {
    category: "kubernetes",
    title: "Kubernetes / Slurm targets",
    blurb:
      "Existing clusters BackStage submits pods/jobs into instead of provisioning fresh VMs.",
  },
  {
    category: "storage",
    title: "Object storage",
    blurb:
      "S3-compatible buckets SkyPilot can mount on replicas for shared model weights and datasets.",
  },
  {
    category: "registry",
    title: "Model & dataset registry",
    blurb:
      "Credentials passed to replicas so they can pull gated artifacts at boot time.",
  },
  {
    category: "container_registry",
    title: "Container image registry",
    blurb:
      "Credentials SkyPilot uses to `docker login` before pulling the workload image. Match a row's host (e.g. ghcr.io) to the prefix of `Service.workload.image`; the controller injects SKYPILOT_DOCKER_{USERNAME,PASSWORD,SERVER} on rendered services automatically.",
  },
];

export function providersByCategory(category: ProviderCategory): ProviderKind[] {
  return PROVIDER_KINDS.filter((k) => PROVIDER_META[k].category === category);
}

// ---- entry shape (server-driven) ----------------------------------------
//
// `ProviderEntry` is the *resolved* view a UI row needs: enabled + values
// + fingerprint. The DB row id is exposed as `id` so callers can rotate /
// delete a specific row without re-deriving the (kind, scope, parents)
// tuple.

export interface ProviderEntry {
  /** DB row id; null when the row doesn't exist yet at this scope. */
  id: number | null;
  /** false = explicitly disabled at this scope. */
  enabled: boolean;
  /**
   * Plain (non-secret) field values returned by the controller. Secret
   * fields never appear here — see `secret_keys` for the names of
   * fields that have a stored value the operator can rotate.
   */
  values: Record<string, string>;
  /** Names of secret fields that have a value at this scope. */
  secret_keys: string[];
  /** Last 4 chars of the catalog's `primaryField`; null when unset. */
  key_last4: string | null;
  /** Operator note shown next to the entry (e.g. "prod account"). */
  note?: string | null;
  /** ISO timestamp of the last secret rotation. */
  rotated_at?: string | null;
  /**
   * Outcome of the controller's last `sky check <kind>` against the
   * on-disk credentials synced from this row. Only populated for
   * global-scope rows — service/pool overrides don't reach
   * SkyPilot's filesystem so the status stays null there. See
   * `ProviderSkyCheckStatus` for the enum values.
   */
  sky_check_status?: ProviderSkyCheckStatus | null;
  /** Captured (and truncated) stdout/stderr from the last check. */
  sky_check_message?: string | null;
  /** ISO timestamp of the last `sky check` result. */
  sky_check_at?: string | null;
}

// Service- and pool-scoped entries are the same shape as global
// ``ProviderEntry`` ones. The legacy per-row ``inherit`` flag was
// retired alongside alembic ``0032_drop_provider_inherit``: a
// service / pool is in ``inherit`` mode iff it has zero rows, ``own``
// iff it has at least one. Branch on row presence (or use
// :func:`serviceProvidersMode` / :func:`poolProvidersMode`) rather
// than reading any inherit flag.

export type ScopeProviderMap<E> = Partial<Record<ProviderKind, E>>;

export interface GlobalProviderState {
  entries: ScopeProviderMap<ProviderEntry>;
}

export interface ServiceProviderState {
  serviceId: number;
  entries: ScopeProviderMap<ProviderEntry>;
}

export interface PoolProviderState {
  poolId: number;
  /** Per-cloud override entries keyed by `ProviderKind`. Each row at
   *  pool scope counts as an override regardless of its enabled flag —
   *  see the inheritance-by-presence model documented above. */
  overrides: Partial<Record<ProviderKind, ProviderEntry>>;
}

export interface ProvidersDoc {
  global: GlobalProviderState;
  byService: ServiceProviderState[];
  byPool: PoolProviderState[];
}

// ---- query --------------------------------------------------------------

const PROVIDERS_KEY = ["providers"] as const;

/**
 * Invalidate every pool-scoped React Query that depends on the
 * Provider config inheritance chain — currently:
 *
 *  * ``["pools", id, "placements"]``       — :func:`usePoolPlacements`
 *  * ``["pools", id, "resource-catalog"]`` — :func:`usePoolResourceCatalog`
 *
 * Both queries derive their data from the (Global / Service / Pool
 * override) provider chain via the controller; without this, a fresh
 * provider toggle wouldn't be reflected in the Pool Spec picker's
 * "enabled" chips or the placements preview until the staleTime
 * elapsed. We use a predicate rather than a partial-key invalidate
 * because other ``["pools", id, ...]`` queries (``workload``,
 * ``provisioning-errors``, ``replica-runtime``) have their own
 * narrower invalidation paths and don't need to be refetched on
 * every provider edit.
 */
function invalidateProviderDependentPoolQueries(
  qc: ReturnType<typeof useQueryClient>,
): void {
  qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      if (!Array.isArray(k) || k.length < 3) return false;
      return (
        k[0] === "pools" &&
        (k[2] === "placements" || k[2] === "resource-catalog")
      );
    },
  });
}

function rowToEntry(row: ProviderOut): ProviderEntry {
  return {
    id: row.id,
    enabled: row.enabled,
    values: { ...(row.values ?? {}) },
    secret_keys: [...(row.secret_keys ?? [])],
    key_last4: row.key_last4,
    note: row.note,
    rotated_at: row.rotated_at,
    sky_check_status: row.sky_check_status,
    sky_check_message: row.sky_check_message,
    sky_check_at: row.sky_check_at,
  };
}

function buildDoc(rows: ProviderOut[]): ProvidersDoc {
  const global: GlobalProviderState = { entries: {} };
  const byServiceMap = new Map<number, ServiceProviderState>();
  const byPoolMap = new Map<number, PoolProviderState>();

  for (const row of rows) {
    const kind = row.kind as ProviderKind;
    if (row.scope === "global") {
      global.entries[kind] = rowToEntry(row);
    } else if (row.scope === "service" && row.service_id != null) {
      const sid = row.service_id;
      const existing = byServiceMap.get(sid) ?? {
        serviceId: sid,
        entries: {} as ScopeProviderMap<ProviderEntry>,
      };
      existing.entries[kind] = rowToEntry(row);
      byServiceMap.set(sid, existing);
    } else if (row.scope === "pool" && row.pool_id != null) {
      // A pool may span multiple clouds (one per placement), so the
      // override store now keys per-(pool, kind). The PoolProviders UI
      // renders one section per unique cloud appearing in the pool's
      // placements and reads/writes that cloud's row through this map.
      const pid = row.pool_id;
      const existing = byPoolMap.get(pid) ?? {
        poolId: pid,
        overrides: {} as Partial<Record<ProviderKind, ProviderEntry>>,
      };
      existing.overrides[kind] = rowToEntry(row);
      byPoolMap.set(pid, existing);
    }
  }

  return {
    global,
    byService: Array.from(byServiceMap.values()),
    byPool: Array.from(byPoolMap.values()),
  };
}

const EMPTY_DOC: ProvidersDoc = {
  global: { entries: {} },
  byService: [],
  byPool: [],
};

// Shared `useQuery` invocation behind both `useProviders()` and
// `useProvidersStatus()`. React Query dedupes by `queryKey`, so calling
// both hooks from the same component fires the network request once
// while still letting each hook return its own narrow projection.
function useProvidersQuery() {
  return useQuery({
    queryKey: PROVIDERS_KEY,
    queryFn: () => api.get<ProviderOut[]>("/providers"),
    staleTime: 30_000,
    // The controller now runs `sky check` in a BackgroundTask after
    // every provider write — the PUT/POST returns immediately with
    // `sky_check_status="pending"` and the real outcome is persisted
    // a few seconds later. Without this poll the WebUI would show the
    // "checking…" pill forever (or until the operator manually
    // refreshes / triggers another mutation): we'd never observe the
    // status flip from pending → ok / failed. Polling only while at
    // least one row is pending keeps the network chatter bounded —
    // the steady-state Providers page issues zero background fetches
    // beyond React Query's normal stale-time refresh.
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (!rows) return false;
      const hasPending = rows.some(
        (r) => r.sky_check_status === "pending",
      );
      return hasPending ? 2_500 : false;
    },
    refetchIntervalInBackground: false,
  });
}

/** Subscribe to every provider config row. Cached for 30s. */
export function useProviders(): ProvidersDoc {
  const q = useProvidersQuery();
  return useMemo(() => (q.data ? buildDoc(q.data) : EMPTY_DOC), [q.data]);
}

/**
 * Loading state for the `/providers` query, exposed so the Providers
 * tabs can render a "loading…" placeholder instead of falling through
 * to "not configured" while the very first fetch is still in flight.
 *
 * The distinction matters because `useProviders()` synthesises an
 * `EMPTY_DOC` when `q.data` is undefined — without this companion
 * hook the catalog rows are indistinguishable from a genuinely
 * unconfigured deployment, which has bitten operators who flipped a
 * toggle off in panic before the data arrived.
 *
 * `isLoading` mirrors React Query v5's `isPending`: true on the very
 * first fetch (no data, not disabled) and flips back to false once we
 * have anything in the cache. Subsequent background refetches do NOT
 * set it back to true — those are surfaced through `isFetching` for
 * callers that care about the "refreshing" badge.
 */
export interface ProvidersStatus {
  /** True while the very first `/providers` fetch is in flight. */
  isLoading: boolean;
  /** True whenever a `/providers` fetch is in flight (initial or refetch). */
  isFetching: boolean;
}

export function useProvidersStatus(): ProvidersStatus {
  const q = useProvidersQuery();
  return { isLoading: q.isPending, isFetching: q.isFetching };
}

// ---- region suggestions (live from SkyPilot via the controller) ---------
//
// We deliberately do NOT carry a hand-edited region list per provider in
// this bundle: that's two catalogs to keep in sync (ours + SkyPilot's),
// and SkyPilot already has the canonical answer baked into its service
// catalog CSVs. The controller exposes the live data at:
//   - `GET /providers/regions/{kind}`         — bare regions
//   - `GET /providers/regions/{kind}/zones`   — (region, zone) pairs
// This hook merges both into a single suggestion list that the
// RegionsCell instances on Global / Service / Pool provider pages
// consume. Bare regions are listed first; zone-pinned entries follow
// (formatted as `REGION:ZONE`) and let operators short-circuit
// SkyPilot's RunPod adapter zone-join bug — see
// `controller/skypilot/placements.py` module docstring.
//
// The result is cached for an hour client-side; the controller itself
// caches in-process for the lifetime of the Pod, which lines up with
// SkyPilot's own ~8h catalog refresh cadence. An empty list is the
// expected response for Kubernetes-style targets and for any cloud
// whose SkyPilot adapter doesn't ship a region table — RegionsCell
// just degrades to a free-form input in that case.

interface RegionSuggestion {
  value: string;
  label?: string;
}

interface ZoneRow {
  region: string;
  zone: string;
}

/**
 * Separator between the region and zone halves of a `REGION:ZONE`
 * `enabled_regions` entry. Mirrors `REGION_ZONE_SEPARATOR` on the
 * controller side (`controller/skypilot/placements.py`); both ends
 * MUST agree or zone-pinning silently degrades to a malformed region.
 */
export const REGION_ZONE_SEPARATOR = ":";

/**
 * Split a single `enabled_regions` entry into its region and optional
 * zone halves. Free-form entries without a separator round-trip as
 * `{ region: value, zone: null }`. Used by RegionsCell to render a
 * subtle visual cue on zone-pinned chips so operators can tell at a
 * glance which entries are bypassing the SkyPilot multi-zone bug.
 */
export function parseRegionZone(value: string): {
  region: string;
  zone: string | null;
} {
  const idx = value.indexOf(REGION_ZONE_SEPARATOR);
  if (idx < 0) return { region: value, zone: null };
  const region = value.slice(0, idx);
  const zone = value.slice(idx + 1);
  return { region, zone: zone.length > 0 ? zone : null };
}

const REGIONS_EMPTY: ReadonlyArray<RegionSuggestion> = Object.freeze([]);

interface RegionsBundle {
  regions: RegionSuggestion[];
  zones: ZoneRow[];
}

type AllRegionsResponse = Record<string, RegionsBundle>;

const ALL_REGIONS_KEY = ["providers", "regionSuggestions", "all"] as const;

/**
 * Single bulk query for every supported cloud's region/zone catalog.
 *
 * The Providers tab renders ~18 rows that each previously kicked off
 * two fetches (``/providers/regions/{kind}`` + ``…/zones``) — 36
 * round trips on first paint, which (a) saturated the browser's
 * per-host connection limit and (b) queued the unrelated
 * ``GET /providers`` request behind a series of SkyPilot catalog
 * parses on the controller's event loop. Operators saw the symptom
 * as the "loading…" pill lingering for several seconds after opening
 * the catalog tab.
 *
 * We now fold both into a single ``GET /providers/regions`` call that
 * returns ``{ [kind]: { regions, zones } }``. React Query dedupes
 * across every ``useCloudRegions`` caller in the tree, so even with
 * 18 ProviderRow instances mounted at once the network sees exactly
 * one request, served off the controller's warm LRU after the
 * startup prewarm.
 *
 * Long-lived caching (1h stale / 24h GC) is fine because SkyPilot's
 * own catalog refresh cycle is on the order of 8 hours; a controller
 * pod restart re-warms the cache and bumps the stable region list.
 */
function useAllCloudRegions() {
  return useQuery({
    queryKey: ALL_REGIONS_KEY,
    queryFn: () => api.get<AllRegionsResponse>("/providers/regions"),
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function useCloudRegions(
  kind: ProviderKind | undefined,
): ReadonlyArray<RegionSuggestion> {
  const enabled = kind != null && (PROVIDER_META[kind]?.supportsRegions ?? false);
  // Always subscribe to the bulk query — every row on the Providers
  // page needs it, and React Query dedupes the actual network call
  // across the sibling subscriptions. Reading a per-kind slice off
  // the cached map is then a pure derive, no extra network.
  const q = useAllCloudRegions();
  return useMemo(() => {
    if (!enabled || !kind || !q.data) return REGIONS_EMPTY;
    const bundle = q.data[kind];
    if (!bundle) return REGIONS_EMPTY;
    const merged: RegionSuggestion[] = bundle.regions.map((r) => ({
      value: r.value,
      label: r.label ?? undefined,
    }));
    // Stable sort by (region, zone) so the autocomplete order matches
    // what the operator just saw on the previous page load.
    const sortedZones = [...bundle.zones].sort((a, b) => {
      if (a.region !== b.region) return a.region.localeCompare(b.region);
      return a.zone.localeCompare(b.zone);
    });
    for (const { region, zone } of sortedZones) {
      merged.push({
        value: `${region}${REGION_ZONE_SEPARATOR}${zone}`,
        // The label appears as a hint next to the value in most
        // datalist renderings — surface the zone name plainly so
        // operators know what they're picking without parsing the
        // value's colon syntax in their head.
        label: `zone ${zone}`,
      });
    }
    return merged;
  }, [enabled, kind, q.data]);
}

// ---- value helpers ------------------------------------------------------

/**
 * Whether this entry has a "complete" config — every required field
 * (per the catalog) has a value at this scope. For secret fields this
 * checks `secret_keys`; for plain fields it checks `values`.
 *
 * Used by the catalog table's "configured / not configured" pill.
 */
export function isEntryConfigured(
  meta: ProviderMeta,
  entry: ProviderEntry | undefined,
): boolean {
  if (!entry) return false;
  for (const f of meta.fields) {
    if (f.optional) continue;
    if (f.secret) {
      if (!entry.secret_keys.includes(f.key)) return false;
    } else if (!entry.values[f.key]) {
      return false;
    }
  }
  return true;
}

/**
 * Composite health for one provider entry, combining DB completeness
 * with the controller's persisted `sky check <kind>` result.
 *
 *   - `unconfigured`  Catalog says a required field is missing. The DB
 *                     row may or may not exist; either way SkyPilot
 *                     cannot authenticate.
 *   - `disabled`      Configured + the enabled toggle is off. We
 *                     treat this as "intentionally muted" rather than
 *                     a failure.
 *   - `pending`       Configured + enabled, but the controller hasn't
 *                     finished running `sky check` yet (either fresh
 *                     write, or pre-migration row without status).
 *   - `failed`        Configured + enabled, but `sky check` reported
 *                     a non-zero exit. Operator should expand the
 *                     status pill to read the captured message.
 *   - `ok`            Configured + enabled + last check passed. This
 *                     is the only state that means "SkyPilot can use
 *                     this cloud right now".
 *   - `skipped`       Configured + enabled, but the controller doesn't
 *                     ship a credential writer for this kind yet
 *                     (B2 backlog). The row exists in the DB but
 *                     never reaches `$HOME`, so SkyPilot won't see
 *                     it. The UI surfaces this as "not yet synced"
 *                     so the operator doesn't think the green pill
 *                     from `isEntryConfigured` means anything.
 *
 * The UI's "configured" pill maps directly onto these states; nothing
 * else should be inventing parallel heuristics for "is this provider
 * actually usable".
 */
export type ProviderHealth =
  | "ok"
  | "failed"
  | "pending"
  | "skipped"
  | "disabled"
  | "unconfigured";

export function providerHealth(
  meta: ProviderMeta,
  entry: ProviderEntry | undefined,
): ProviderHealth {
  if (!isEntryConfigured(meta, entry)) return "unconfigured";
  if (!entry?.enabled) return "disabled";
  switch (entry.sky_check_status) {
    case "ok":
      return "ok";
    case "failed":
      return "failed";
    case "pending":
      return "pending";
    case "skipped":
      return "skipped";
    default:
      // null = the controller has never run a check against this row.
      // This is normal during the first ~second after a credential
      // write and on legacy rows that pre-date the migration. We
      // show "checking…" rather than green so the operator doesn't
      // mistake "row exists" for "SkyPilot can use it".
      return "pending";
  }
}

export function fieldDisplayValue(
  field: ProviderField,
  value: string | undefined,
): string {
  if (!value) return "";
  if (field.secret) {
    return field.type === "textarea" ? `•••• (…${value})` : `••••${value}`;
  }
  return value;
}

// ---- regions allow-list helpers -----------------------------------------
//
// The per-scope region allow-list is persisted in `ProviderEntry.values`
// under the well-known key `enabled_regions`, encoded as a
// comma-separated list. Storing it inside `values` (rather than a
// dedicated column) means the existing provider upsert endpoint keeps
// working without backend changes — the controller treats `values` as
// an opaque map. The controller's placement resolver
// (`controller/skypilot/placements.py`) is the canonical interpreter:
// it splits the CSV the same way and accepts both bare `REGION` and
// `REGION:ZONE` entries (the latter pinning a workload to one
// SkyPilot availability zone, used as a workaround for the RunPod
// adapter's multi-zone CSV-join bug — see that module's docstring).
//
// An empty / missing entry means "all regions" (no restriction). This
// matches the existing behaviour where a Pool placement with
// `region == null` means "anywhere inside this cloud".

// Legacy unsuffixed key, retained as a read-side fallback for rows the
// alembic migration (``...drop_spot_policy.py``) hasn't rewritten yet
// or rows that were edited by hand. Writes always go through the
// variant-suffixed key shape — see ``variantRegionsKey``.
const LEGACY_ENABLED_REGIONS_KEY = "enabled_regions";

function variantRegionsKey(variantKey: string): string {
  return `enabled_regions__${variantKey}`;
}

function variantEnabledKey(variantKey: string): string {
  return `variant_enabled__${variantKey}`;
}

function csvToList(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Read the region allow-list this entry has stored for ``variantKey``.
 *
 * ``variantKey`` defaults to the kind's *default* variant (the one
 * marked ``isDefault: true`` in :data:`PROVIDER_META[kind].variants`),
 * which is the only variant the WebUI surfaces in P0. P1+ flips on the
 * variant tabs and starts passing a non-default key through.
 *
 * Falls back to the legacy unsuffixed ``enabled_regions`` value when:
 *   * the row has no variant-suffixed entries at all (pre-migration
 *     row, or a kind without a placement axis), AND
 *   * we're reading the default variant (callers asking for a
 *     non-default variant should not see the legacy bucket leak in).
 */
export function getEntryRegions(
  entry: ProviderEntry | undefined,
  kind?: ProviderKind,
  variantKey?: string,
): string[] {
  if (!entry) return [];
  const values = entry.values ?? {};
  const variants = kind ? variantsForKind(kind) : [];
  const targetKey =
    variantKey ?? (kind ? defaultVariantForKind(kind)?.key : undefined);

  if (targetKey) {
    const direct = values[variantRegionsKey(targetKey)];
    if (direct !== undefined) {
      return csvToList(direct);
    }
    // Legacy fallback only for the default variant, only when no
    // variant-suffixed entries exist on this row.
    const isDefault = variants.find((v) => v.key === targetKey)?.isDefault;
    const hasAnyVariantKey = variants.some(
      (v) => values[variantRegionsKey(v.key)] !== undefined,
    );
    if (isDefault && !hasAnyVariantKey) {
      return csvToList(values[LEGACY_ENABLED_REGIONS_KEY]);
    }
    return [];
  }
  // No kind / no variant info — fall through to the legacy bucket so
  // single-variant kinds (and tests that don't pass kind through)
  // keep working.
  return csvToList(values[LEGACY_ENABLED_REGIONS_KEY]);
}

/**
 * Build the partial ``values`` patch that persists ``regions`` under
 * the variant-suffixed key shape, plus a ``variant_enabled__*`` flag
 * so the controller knows the variant is active.
 *
 * In P0 the WebUI only writes the *default* variant; the patch keeps
 * shape stable for P1+ which will surface other variants by passing a
 * non-default ``variantKey`` through.
 *
 * For kinds without a variant axis (``variantsForKind(kind)`` empty —
 * kubernetes / object-storage / registries) we fall back to the legacy
 * unsuffixed shape since those rows never grow a variant suffix.
 */
export function regionsToValuePatch(
  regions: string[],
  kind?: ProviderKind,
  variantKey?: string,
): Record<string, string> {
  const cleaned = Array.from(
    new Set(regions.map((r) => r.trim()).filter((r) => r.length > 0)),
  );
  const csv = cleaned.join(",");
  if (!kind) {
    return { [LEGACY_ENABLED_REGIONS_KEY]: csv };
  }
  const variants = variantsForKind(kind);
  if (variants.length === 0) {
    return { [LEGACY_ENABLED_REGIONS_KEY]: csv };
  }
  const target =
    variantKey ?? defaultVariantForKind(kind)?.key ?? variants[0]?.key;
  if (!target) {
    return { [LEGACY_ENABLED_REGIONS_KEY]: csv };
  }
  return {
    [variantRegionsKey(target)]: csv,
    [variantEnabledKey(target)]: "true",
  };
}

/**
 * Build a values-patch that sets *both* the variant's region list and
 * its enabled flag.
 *
 * Used by the per-variant editor on the providers tabs: every checkbox
 * toggle / region edit lands here so the controller writes both halves
 * of the variant axis in one PUT (preserving optimistic atomicity in
 * the providers cache).
 */
export function variantPatch(
  kind: ProviderKind,
  variantKey: string,
  regions: string[],
  enabled: boolean,
): Record<string, string> {
  return {
    ...regionsToValuePatch(regions, kind, variantKey),
    [variantEnabledKey(variantKey)]: enabled ? "true" : "false",
  };
}

// ---- effective-config accessors -----------------------------------------

export function getEffectiveServiceEntry(
  doc: ProvidersDoc,
  serviceId: number,
  kind: ProviderKind,
): ProviderEntry & { inherited: boolean } {
  const svc = doc.byService.find((s) => s.serviceId === serviceId);
  const localEntry = svc?.entries[kind];
  const parent: ProviderEntry = doc.global.entries[kind] ?? {
    id: null,
    enabled: false,
    values: {},
    secret_keys: [],
    key_last4: null,
  };
  if (!localEntry) {
    return { ...parent, inherited: true };
  }
  return { ...localEntry, inherited: false };
}

export function getEffectivePoolEntry(
  doc: ProvidersDoc,
  poolId: number,
  serviceId: number,
  kind: ProviderKind,
): ProviderEntry & { inherited: boolean } {
  const pool = doc.byPool.find((p) => p.poolId === poolId);
  const override = pool?.overrides?.[kind];
  if (override) {
    return { ...override, inherited: false };
  }
  return getEffectiveServiceEntry(doc, serviceId, kind);
}

/**
 * Effective region allow-list, resolved through the inheritance chain.
 *
 * The `inherited` flag follows the same convention as the *_entry
 * helpers above — it's true when the list was read from the parent
 * scope rather than this scope's own override. Callers (the inline
 * editor) use it to surface "inherits from <parent>" hints.
 */
export function getEffectiveRegions(
  doc: ProvidersDoc,
  scope:
    | { kind: "global" }
    | { kind: "service"; serviceId: number }
    | { kind: "pool"; poolId: number; serviceId: number },
  providerKind: ProviderKind,
  variantKey?: string,
): { regions: string[]; inherited: boolean; source: "global" | "service" | "pool" } {
  if (scope.kind === "pool") {
    const pool = doc.byPool.find((p) => p.poolId === scope.poolId);
    const override = pool?.overrides?.[providerKind];
    if (override) {
      return {
        regions: getEntryRegions(override, providerKind, variantKey),
        inherited: false,
        source: "pool",
      };
    }
    const parent = getEffectiveRegions(
      doc,
      { kind: "service", serviceId: scope.serviceId },
      providerKind,
      variantKey,
    );
    return { ...parent, inherited: true };
  }
  if (scope.kind === "service") {
    const svc = doc.byService.find((s) => s.serviceId === scope.serviceId);
    const local = svc?.entries[providerKind];
    if (local) {
      return {
        regions: getEntryRegions(local, providerKind, variantKey),
        inherited: false,
        source: "service",
      };
    }
    const parent = getEffectiveRegions(
      doc,
      { kind: "global" },
      providerKind,
      variantKey,
    );
    return { ...parent, inherited: true };
  }
  return {
    regions: getEntryRegions(
      doc.global.entries[providerKind],
      providerKind,
      variantKey,
    ),
    inherited: false,
    source: "global",
  };
}

// ---- mutation hooks -----------------------------------------------------

/**
 * Patch shape consumed by `useUpdateGlobalEntry` / service / pool.
 *
 *   - `enabled` / `note` are passed through.
 *   - `values` is a partial dict of edits the operator just typed; the
 *     controller merges it with the stored secrets (empty string ==
 *     "clear that field"). Non-secret fields are *replaced* on every
 *     write (the form submits all of them, full value, on every save).
 *
 * The legacy per-row ``inherit`` field was retired alongside alembic
 * ``0032_drop_provider_inherit``: inherit/own is now a page-level
 * toggle expressed by detach/inherit-again endpoints, not a per-row
 * patch field.
 */
export interface ProviderEntryPatch {
  enabled?: boolean;
  note?: string | null;
  values?: Record<string, string>;
  /**
   * When true, the controller will (after persisting the row, syncing
   * `$HOME`, and re-running `sky check`) walk every Pool whose resolved
   * placements actually mention this provider kind and drop their
   * cached `last_applied_yaml_hash` so the next reconcile tick fires
   * a `sky.serve.update(..., mode='ROLLING')` against the freshly
   * written on-disk credentials.
   *
   * This is the only way to push a refreshed credential set onto a
   * running SkyServe controller VM today (SkyPilot has no
   * "credentials only" refresh API). It implies a rolling update of
   * every affected service: zero downtime, but a temporary
   * provisioning-cost spike and KV-cache loss as old replicas drain.
   *
   * Forwarded as the `?propagate=true` query parameter to the
   * controller's `PUT /providers/{kind}` (and `POST /rotate`)
   * endpoints. Defaults to `false` to preserve the historical
   * "save-only, don't touch running services" behaviour.
   */
  propagate?: boolean;
}

function buildUpsertBody(
  meta: ProviderMeta,
  scope: { service_id: number | null; pool_id: number | null },
  current: ProviderEntry | undefined,
  patch: ProviderEntryPatch,
): ProviderUpsertBody {
  // Merge non-secret values so a patch that only flips `enabled` doesn't
  // wipe the stored region/account-id. Secret values are *not* in
  // `current.values` (they live on the server), so the merge naturally
  // leaves them alone unless the operator typed something new.
  const mergedValues: Record<string, string> = {
    ...(current?.values ?? {}),
    ...(patch.values ?? {}),
  };
  const secretKeys = meta.fields.filter((f) => f.secret).map((f) => f.key);
  const primaryKey = meta.fields.find((f) => f.key === meta.primaryField)
    ?.secret
    ? meta.primaryField
    : null;
  return {
    service_id: scope.service_id,
    pool_id: scope.pool_id,
    enabled:
      patch.enabled !== undefined
        ? patch.enabled
        : (current?.enabled ?? false),
    note: patch.note !== undefined ? patch.note : (current?.note ?? null),
    values: mergedValues,
    secret_keys: secretKeys,
    primary_secret_key: primaryKey,
  };
}

// ---- scope-level inherit / detach (Workload-style) ----------------------
//
// Mirrors the Workload feature on the Pool / Service Providers pages:
// the entire scope is either ``own`` (one ``ProviderConfig`` row per
// kind, copied from the parent on detach) or ``inherit`` (no rows; the
// effective config is read straight from the parent's resolved entry).
// There is no per-kind inherit toggle anymore — the row's existence
// IS the mode flag, exactly the way ``Pool.workload IS NULL ⇔
// inherit`` works for the Workload page.

export type ProvidersMode = "inherit" | "own";

export interface ProviderScopeOpResult {
  mode: ProvidersMode;
  count: number;
  changed: number;
}

/**
 * Whether the given service has any service-scoped rows. After
 * alembic ``0032_drop_provider_inherit`` the row's existence IS the
 * mode flag — no per-row inherit toggle to filter on.
 */
export function serviceProvidersMode(
  doc: ProvidersDoc,
  serviceId: number,
): ProvidersMode {
  const svc = doc.byService.find((s) => s.serviceId === serviceId);
  if (!svc) return "inherit";
  return Object.keys(svc.entries).length > 0 ? "own" : "inherit";
}

/** Pool-side analogue of :func:`serviceProvidersMode`. */
export function poolProvidersMode(
  doc: ProvidersDoc,
  poolId: number,
): ProvidersMode {
  const pool = doc.byPool.find((p) => p.poolId === poolId);
  if (!pool) return "inherit";
  return Object.keys(pool.overrides).length > 0 ? "own" : "inherit";
}

function useScopeMutation(
  build: (id: number) => string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: PROVIDERS_KEY,
    mutationFn: (id: number) =>
      api.post<ProviderScopeOpResult>(build(id)),
    onSettled: () => {
      // Detach / inherit drop or create rows; the rendered table needs
      // to converge on the controller's truth, so always invalidate.
      // Other in-flight provider mutations (per-kind PUTs) will reuse
      // the same mutationKey gate in `useUpsertProvider` so they don't
      // step on each other.
      if (queryClient.isMutating({ mutationKey: PROVIDERS_KEY }) <= 1) {
        queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
        invalidateProviderDependentPoolQueries(queryClient);
      }
    },
  });
}

export function useDetachServiceProviders() {
  const m = useScopeMutation(
    (id) => `/providers/services/${encodeURIComponent(String(id))}/detach`,
  );
  return useCallback((serviceId: number) => m.mutateAsync(serviceId), [m]);
}

export function useInheritServiceProviders() {
  const m = useScopeMutation(
    (id) => `/providers/services/${encodeURIComponent(String(id))}/inherit`,
  );
  return useCallback((serviceId: number) => m.mutateAsync(serviceId), [m]);
}

export function useDetachPoolProviders() {
  const m = useScopeMutation(
    (id) => `/providers/pools/${encodeURIComponent(String(id))}/detach`,
  );
  return useCallback((poolId: number) => m.mutateAsync(poolId), [m]);
}

export function useInheritPoolProviders() {
  const m = useScopeMutation(
    (id) => `/providers/pools/${encodeURIComponent(String(id))}/inherit`,
  );
  return useCallback((poolId: number) => m.mutateAsync(poolId), [m]);
}

function useUpsertProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    // Tag every provider write with the same key so we can ask
    // `queryClient.isMutating({ mutationKey: PROVIDERS_KEY })` how many
    // sibling writes are still in flight. This is what lets `onSettled`
    // skip the refetch while another edit is queued — the missing piece
    // that caused freshly-added regions to silently revert for the
    // duration of the next round-trip (see `onSettled` below for the
    // full rationale).
    mutationKey: PROVIDERS_KEY,
    mutationFn: async (args: {
      kind: ProviderKind;
      body: ProviderUpsertBody;
      propagate?: boolean;
    }) =>
      api.put<ProviderOut>(
        // Append `?propagate=true` only when the operator opted in via
        // the form's checkbox. Omitting the query keeps the URL clean
        // for the common "just save the row" case and falls through to
        // the controller's default (`propagate=False`), so the request
        // is wire-identical to the pre-feature behaviour.
        `/providers/${encodeURIComponent(args.kind)}${
          args.propagate ? "?propagate=true" : ""
        }`,
        args.body,
      ),
    // Optimistic cache patch so the toggle and "configured" pill flip
    // immediately, even before the controller has finished the round
    // trip. Without this the operator sees a perceptible delay between
    // clicking the switch and the UI reflecting the new state, which
    // reads as "the toggle is broken".
    onMutate: async (args) => {
      // Cancel any in-flight *refetch* (not mutations) so it can't land
      // after our optimistic write and stomp on it.
      await queryClient.cancelQueries({ queryKey: PROVIDERS_KEY });
      const previous = queryClient.getQueryData<ProviderOut[]>(PROVIDERS_KEY);
      if (previous) {
        queryClient.setQueryData<ProviderOut[]>(
          PROVIDERS_KEY,
          patchRows(previous, args.kind, args.body),
        );
      }
      return { previous };
    },
    onSuccess: (data) => {
      // Merge the canonical row the controller just persisted (correct
      // `key_last4`, `rotated_at`, `sky_check_*`, …) directly into the
      // cached list. Doing this *without* a separate refetch is what
      // closes the race window where a refetch triggered by an earlier
      // mutation would land mid-way through a later still-in-flight
      // mutation and overwrite its optimistic state — operators saw
      // this as "the region I just typed disappeared for a second
      // before reappearing", which is exactly the "regions revert on
      // their own moments later" bug report this code path now fixes.
      queryClient.setQueryData<ProviderOut[]>(PROVIDERS_KEY, (prev) => {
        if (!prev) return [data];
        const matchScope = (r: ProviderOut) =>
          r.kind === data.kind &&
          r.service_id === data.service_id &&
          r.pool_id === data.pool_id;
        const idx = prev.findIndex(matchScope);
        if (idx === -1) return [...prev, data];
        const out = prev.slice();
        out[idx] = data;
        return out;
      });
    },
    onError: (_err, _args, _ctx) => {
      // We deliberately do NOT roll back to the `previous` snapshot
      // captured in onMutate: with concurrent edits in flight, that
      // snapshot is older than the *other* mutations' optimistic
      // writes, so restoring it would silently revert unrelated edits
      // that are about to succeed. Invalidating instead lets us
      // converge on the controller's truth once everything settles —
      // which is exactly what the (mutation-aware) onSettled does.
      if (queryClient.isMutating({ mutationKey: PROVIDERS_KEY }) <= 1) {
        queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
        invalidateProviderDependentPoolQueries(queryClient);
      }
    },
    onSettled: () => {
      // Only refetch once *all* in-flight provider mutations have
      // completed. While a sibling write is still pending its
      // optimistic state lives only in the cache, and a refetch
      // launched here would race the still-pending PUT and return
      // pre-edit data — overwriting the optimistic value until the
      // pending PUT finally commits and triggers its own refetch.
      // Operators saw that as freshly-added regions disappearing for a
      // few seconds and then "auto-correcting" themselves. Gating the
      // refetch on `isMutating <= 1` (the count includes *this*
      // mutation, which is about to leave the in-flight set) keeps the
      // optimistic state intact until everyone has settled.
      if (queryClient.isMutating({ mutationKey: PROVIDERS_KEY }) <= 1) {
        queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
        invalidateProviderDependentPoolQueries(queryClient);
      }
    },
  });
}

/**
 * Apply a `ProviderUpsertBody` to the cached `/providers` list as a
 * best-effort optimistic patch.
 *
 * This is intentionally narrow: it only updates fields the controller
 * is guaranteed to honour synchronously (`enabled`, `note`,
 * `values_plain`, `secret_keys`). The fingerprint columns (`key_last4`,
 * `rotated_at`) come from the real refetch in `onSettled`.
 */
function patchRows(
  rows: ProviderOut[],
  kind: ProviderKind,
  body: ProviderUpsertBody,
): ProviderOut[] {
  const matchScope = (r: ProviderOut) =>
    r.kind === kind &&
    r.service_id === body.service_id &&
    r.pool_id === body.pool_id;
  const idx = rows.findIndex(matchScope);
  // Split the submitted values the same way the controller will so the
  // optimistic row mirrors the persisted shape.
  const secretSet = new Set(body.secret_keys);
  const plainValues: Record<string, string> = {};
  const submittedSecretKeys: string[] = [];
  for (const [k, v] of Object.entries(body.values)) {
    const trimmed = (v ?? "").trim();
    if (!trimmed) continue;
    if (secretSet.has(k)) {
      submittedSecretKeys.push(k);
    } else {
      plainValues[k] = trimmed;
    }
  }
  if (idx === -1) {
    // Brand-new row — synth a placeholder until the server returns the
    // canonical shape (with id, timestamps, key_last4).
    const now = new Date().toISOString();
    const placeholder: ProviderOut = {
      id: -1,
      kind,
      scope: body.pool_id != null
        ? "pool"
        : body.service_id != null
          ? "service"
          : "global",
      service_id: body.service_id,
      pool_id: body.pool_id,
      enabled: body.enabled,
      values: plainValues,
      secret_keys: submittedSecretKeys,
      key_last4: null,
      rotated_at: submittedSecretKeys.length ? now : null,
      note: body.note ?? null,
      // Optimistic placeholder seeds the sky-check status as
      // "pending" so the UI shows a "checking…" pill while the
      // round-trip is in flight; the real result overwrites this in
      // onSettled. We deliberately do NOT echo any previous status
      // here — the row is brand new so the previous check is
      // meaningless.
      sky_check_status: "pending",
      sky_check_message: null,
      sky_check_at: now,
      created_at: now,
      updated_at: now,
    };
    return [...rows, placeholder];
  }
  const existing = rows[idx];
  // Preserve any secret keys the server already knew about — the
  // operator may have toggled `enabled` without retyping the key.
  const mergedSecretKeys = Array.from(
    new Set([...existing.secret_keys, ...submittedSecretKeys]),
  );
  const next: ProviderOut = {
    ...existing,
    enabled: body.enabled,
    note: body.note ?? null,
    values: plainValues,
    secret_keys: mergedSecretKeys,
    rotated_at: submittedSecretKeys.length
      ? new Date().toISOString()
      : existing.rotated_at,
    // Flag the row as "checking…" optimistically; onSettled will
    // refetch the canonical state. Without this the previously-
    // rendered "ok" pill would linger until the network round-trip
    // and operator-facing badge wouldn't react to credential edits.
    sky_check_status: "pending",
    sky_check_message: null,
    sky_check_at: new Date().toISOString(),
  };
  const out = rows.slice();
  out[idx] = next;
  return out;
}

function useRotateProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    // Same key as upserts so both flavours of write are counted by the
    // shared `isMutating({ mutationKey: PROVIDERS_KEY })` gate below.
    mutationKey: PROVIDERS_KEY,
    mutationFn: async (args: { id: number; propagate?: boolean }) =>
      api.post<ProviderOut>(
        `/providers/${args.id}/rotate${
          args.propagate ? "?propagate=true" : ""
        }`,
      ),
    onSuccess: (data) => {
      // Splice the canonical row in directly so we don't kick off a
      // refetch that could race a still-in-flight upsert (see the long
      // explanation on `useUpsertProvider.onSettled`).
      queryClient.setQueryData<ProviderOut[]>(PROVIDERS_KEY, (prev) => {
        if (!prev) return [data];
        const idx = prev.findIndex((r) => r.id === data.id);
        if (idx === -1) return [...prev, data];
        const out = prev.slice();
        out[idx] = data;
        return out;
      });
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: PROVIDERS_KEY }) <= 1) {
        queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY });
        invalidateProviderDependentPoolQueries(queryClient);
      }
    },
  });
}

/** Optional knobs for the credential-clear hooks. Mirrors the
 * `propagate` field on `ProviderEntryPatch` so the modal's checkbox
 * toggles the same flag whether the operator hits Save or "clear all
 * credentials". */
export interface ClearCredentialsOptions {
  propagate?: boolean;
}

/**
 * Hook variant of the old `updateGlobalEntry`. Returns a stable callback
 * (kind, patch) -> Promise that hits PUT /providers/{kind}.
 */
export function useUpdateGlobalEntry() {
  const upsert = useUpsertProvider();
  const doc = useProviders();
  return useCallback(
    async (kind: ProviderKind, patch: ProviderEntryPatch) => {
      const meta = PROVIDER_META[kind];
      const current = doc.global.entries[kind];
      // Pluck `propagate` off the patch — it's a request-level query
      // parameter, not a row-level field, so it must not flow through
      // `buildUpsertBody` (which would silently drop it on the way
      // into ProviderUpsertBody anyway, but better to be explicit).
      const { propagate, ...rowPatch } = patch;
      const body = buildUpsertBody(
        meta,
        { service_id: null, pool_id: null },
        current,
        rowPatch,
      );
      await upsert.mutateAsync({ kind, body, propagate });
    },
    [doc, upsert],
  );
}

export function useUpdateServiceEntry() {
  const upsert = useUpsertProvider();
  const doc = useProviders();
  return useCallback(
    async (
      serviceId: number,
      kind: ProviderKind,
      patch: ProviderEntryPatch,
    ) => {
      const meta = PROVIDER_META[kind];
      const current = doc.byService.find((s) => s.serviceId === serviceId)
        ?.entries[kind];
      const { propagate, ...rowPatch } = patch;
      const body = buildUpsertBody(
        meta,
        { service_id: serviceId, pool_id: null },
        current,
        rowPatch,
      );
      await upsert.mutateAsync({ kind, body, propagate });
    },
    [doc, upsert],
  );
}

export function useUpdatePoolOverride() {
  const upsert = useUpsertProvider();
  const doc = useProviders();
  return useCallback(
    async (
      poolId: number,
      serviceId: number,
      kind: ProviderKind,
      patch: ProviderEntryPatch,
    ) => {
      const meta = PROVIDER_META[kind];
      const current = doc.byPool.find((p) => p.poolId === poolId)?.overrides?.[
        kind
      ];
      const { propagate, ...rowPatch } = patch;
      const body = buildUpsertBody(
        meta,
        { service_id: serviceId, pool_id: poolId },
        current ?? undefined,
        rowPatch,
      );
      await upsert.mutateAsync({ kind, body, propagate });
    },
    [doc, upsert],
  );
}

export function useClearGlobalCredentials() {
  const rotate = useRotateProvider();
  const doc = useProviders();
  return useCallback(
    async (kind: ProviderKind, opts?: ClearCredentialsOptions) => {
      const id = doc.global.entries[kind]?.id;
      if (id == null) return;
      await rotate.mutateAsync({ id, propagate: opts?.propagate });
    },
    [doc, rotate],
  );
}

export function useClearServiceCredentials() {
  const rotate = useRotateProvider();
  const doc = useProviders();
  return useCallback(
    async (
      serviceId: number,
      kind: ProviderKind,
      opts?: ClearCredentialsOptions,
    ) => {
      const id = doc.byService.find((s) => s.serviceId === serviceId)?.entries[
        kind
      ]?.id;
      if (id == null) return;
      await rotate.mutateAsync({ id, propagate: opts?.propagate });
    },
    [doc, rotate],
  );
}

export function useClearPoolCredentials() {
  const rotate = useRotateProvider();
  const doc = useProviders();
  return useCallback(
    async (
      poolId: number,
      kind: ProviderKind,
      opts?: ClearCredentialsOptions,
    ) => {
      const id = doc.byPool.find((p) => p.poolId === poolId)?.overrides?.[kind]
        ?.id;
      if (id == null) return;
      await rotate.mutateAsync({ id, propagate: opts?.propagate });
    },
    [doc, rotate],
  );
}
